import YatungDatePicker from '@Src/_basic/components/YatungDatePicker';
import {
  ThirdPartyAccountFormValues,
  ThirdPartyAccountSearchLimit,
  ThirdPartyAccountSearchLimitDate,
  ThirdPartyAccountSearchLimitResponse,
} from '@Src/_basic/object/ThirdPartyAccountType';
import { Box, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EditAccountField from '../../../_components/EditAccountField';
import EditAccountLabel from '../../../_components/EditAccountLabel';

const DATE_FORMAT = 'YYYY-MM-DD';

const toDateString = (date: Date | null) => (date ? moment(date).format(DATE_FORMAT) : null);

// 後端回傳 [年, 月, 日] (月份從1開始), moment 的月份從0開始
const toDate = (value: ThirdPartyAccountSearchLimitDate | null | undefined) => {
  if (!Array.isArray(value) || value.length < 3) return null;
  const [year, month, day] = value;
  const date = moment([year, month - 1, day]);
  return date.isValid() ? date.toDate() : null;
};

// 表單值 -> 限制搜尋資料日期API的request
export const toSearchLimitRequest = (values: ThirdPartyAccountFormValues): ThirdPartyAccountSearchLimit => ({
  account: values.account,
  limit_request_date_to_now: values.searchLimitDays ? Number(values.searchLimitDays) : 0,
  limit_request_date_range_start: toDateString(values.searchStartDate),
  limit_request_date_range_end: toDateString(values.searchEndDate),
});

// 限制搜尋資料日期API的response -> 表單值
export const toSearchLimitFormValues = (data?: Partial<ThirdPartyAccountSearchLimitResponse> | null) => ({
  searchLimitDays:
    data?.limit_request_date_to_now !== undefined && data.limit_request_date_to_now !== null
      ? `${data.limit_request_date_to_now}`
      : '0',
  searchStartDate: toDate(data?.limit_request_date_range_start),
  searchEndDate: toDate(data?.limit_request_date_range_end),
});

// 新增/編輯頁面共用：限制搜尋資料日期距今日數、日期區間起訖
export default function ThirdPartyAccountSearchLimitFields() {
  const { t: i18T } = useTranslation();
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<ThirdPartyAccountFormValues>();

  const getDateError = (target: 'searchStartDate' | 'searchEndDate') =>
    touched[target] && errors[target] ? `${errors[target]}` : undefined;

  const handleDateChange = (target: 'searchStartDate' | 'searchEndDate') => (value: Date | null) => {
    setFieldValue(target, value && !isNaN(value.getTime()) ? value : null);
    setFieldTouched(target, true, false);
  };

  return (
    <>
      <Stack direction="row" sx={{ mt: 3 }}>
        <EditAccountLabel label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.SEARCH_LIMIT_DAYS')} />
        <EditAccountField target="searchLimitDays" />
      </Stack>
      <Stack direction="row" sx={{ mt: 3 }}>
        <EditAccountLabel label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.SEARCH_START_DATE')} />
        <Box sx={{ width: '450px', minWidth: '450px' }}>
          <YatungDatePicker
            fullWidth
            value={values.searchStartDate}
            onChange={handleDateChange('searchStartDate')}
            error={getDateError('searchStartDate')}
          />
        </Box>
      </Stack>
      <Stack direction="row" sx={{ mt: 3 }}>
        <EditAccountLabel label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.SEARCH_END_DATE')} />
        <Box sx={{ width: '450px', minWidth: '450px' }}>
          <YatungDatePicker
            fullWidth
            value={values.searchEndDate}
            onChange={handleDateChange('searchEndDate')}
            error={getDateError('searchEndDate')}
          />
        </Box>
      </Stack>
    </>
  );
}
